/**
 * "You're about to be signed out" dialog (WS8 / HIPAA automatic logoff).
 *
 * Mounted once, in `AppShell` — the only authenticated wrapper — so it is
 * present on every page that can render PHI. There is no other modal, portal
 * or context provider in that shell, so this component owns its own overlay
 * rather than borrowing a pattern that does not exist.
 *
 * Two modes, driven by which deadline is expiring:
 *
 *   idle      → extendable. Primary action re-arms the session.
 *   absolute  → NOT extendable. The session has reached its maximum length;
 *               the only way forward is a fresh sign-in. Offering a
 *               "Stay signed in" button here would be a lie, so the primary
 *               action re-authenticates instead.
 *
 * Motion is opt-in via `motion-safe:` so `prefers-reduced-motion: reduce`
 * gets a static dialog and a countdown bar that jumps rather than slides.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert } from 'lucide-react'
import { useIdleTimeout } from '../../hooks/useIdleTimeout'

function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds)
  const minutes = Math.floor(safe / 60)
  const seconds = safe % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export function SessionTimeoutModal() {
  const { t } = useTranslation('auth')
  const {
    state,
    reason,
    secondsRemaining,
    warningSeconds,
    canExtend,
    extend,
    endSession,
  } = useIdleTimeout()
  const primaryRef = useRef<HTMLButtonElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const open = state === 'warning'

  // Move focus into the dialog when it appears, and keep Tab inside it.
  // A full focus-trap library would be overkill for two buttons.
  useEffect(() => {
    if (!open) return
    primaryRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>('button')
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open])

  if (!open) return null

  const isAbsolute = reason === 'absolute'
  const countdown = formatCountdown(secondsRemaining)

  // Fraction of the warning window still left. Denominator is the configured
  // warning lead, not the remaining time — using the latter would pin the bar
  // at 100% forever. Falls back to a full bar if the window is zero-length.
  const widthPct =
    warningSeconds > 0
      ? Math.min(100, Math.max(0, (secondsRemaining / warningSeconds) * 100))
      : 100

  // Announce in 30s buckets. An aria-live region that changed once a second
  // would talk over everything else a screen-reader user is doing.
  const announcedSeconds = Math.max(0, Math.ceil(secondsRemaining / 30) * 30)

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 motion-safe:transition-opacity motion-safe:duration-150"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-body"
        className="w-full max-w-md overflow-hidden rounded-lg border border-border bg-surface shadow-xl motion-safe:transition-transform motion-safe:duration-150"
      >
        <header className="flex items-start gap-3 border-b border-border px-5 py-4">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <div>
            <h2 id="session-timeout-title" className="font-medium text-text-primary">
              {isAbsolute
                ? t('sessionTimeout.absoluteTitle')
                : t('sessionTimeout.idleTitle')}
            </h2>
            <p id="session-timeout-body" className="mt-1 text-sm text-text-secondary">
              {isAbsolute
                ? t('sessionTimeout.absoluteBody')
                : t('sessionTimeout.idleBody')}
            </p>
          </div>
        </header>

        <div className="space-y-2 px-5 py-4">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-text-secondary">
              {t('sessionTimeout.signingOutIn')}
            </span>
            <span
              className="font-mono text-2xl tabular-nums text-text-primary"
              aria-hidden="true"
            >
              {countdown}
            </span>
          </div>
          <div
            className="h-1.5 w-full overflow-hidden rounded-full bg-border-light"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(widthPct)}
            aria-label={t('sessionTimeout.signingOutIn')}
          >
            <div
              className="h-full rounded-full bg-warning motion-safe:transition-[width] motion-safe:duration-1000 motion-safe:ease-linear"
              style={{ width: `${widthPct}%` }}
            />
          </div>
          {/* Screen readers get the remaining time in words rather than a
              once-a-second numeric update, which would be unusable. */}
          <p className="sr-only" aria-live="polite">
            {t('sessionTimeout.remainingAnnouncement', { count: announcedSeconds })}
          </p>
          <p className="text-xs text-text-muted">
            {t('sessionTimeout.whyFooter')}
          </p>
        </div>

        <footer className="flex justify-end gap-2 border-t border-border bg-surface-elevated px-5 py-3">
          <button
            type="button"
            onClick={endSession}
            className="rounded border border-border px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-card"
          >
            {t('sessionTimeout.signOutNow')}
          </button>
          <button
            ref={primaryRef}
            type="button"
            onClick={() => {
              if (canExtend) {
                extend()
              } else {
                // Absolute cap: nothing to extend. Send the browser through
                // AuthKit with force_login so a fresh session is actually
                // re-authenticated rather than silently re-issued.
                const next = window.location.pathname + window.location.search
                window.location.assign(
                  `/auth/workos/start?next=${encodeURIComponent(next)}&force_login=1`
                )
              }
            }}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-hover"
          >
            {canExtend
              ? t('sessionTimeout.staySignedIn')
              : t('sessionTimeout.signInAgain')}
          </button>
        </footer>
      </div>
    </div>
  )
}
